import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSuggestedQuestionsQuery } from "./SuggestedQuestions.generated";
import { cn } from "@src/lib/utils/cn";
import LoadingIcon from "@src/lib/icons/18px/loading";

import Sparkles2OutlineIcon from "@src/lib/icons/12px/sparkles-2-outline";

interface SuggestedQuestionsDisplayProps {
  onQuestionSelect: (questionId: string, questionText: string) => void;
  isVisible: boolean;
  className?: string;
}

export const SuggestedQuestionsDisplay: React.FC<
  SuggestedQuestionsDisplayProps
> = ({ onQuestionSelect, isVisible, className = "" }) => {
  const { data, loading, error } = useSuggestedQuestionsQuery();

  if (!isVisible) {
    return null;
  }

  if (loading) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center py-8 space-y-4",
          className,
        )}
      >
        <LoadingIcon className="animate-spin text-gray-6" />
        <p className="animate-pulse text-sm text-muted-foreground">
          Loading suggestions...
        </p>
      </div>
    );
  }

  if (
    error ||
    !data?.SuggestedQuestions ||
    data.SuggestedQuestions.objects.length === 0
  ) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
        className={cn("flex w-full min-w-0 flex-col gap-2", className)}
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.3 }}
          className="px-1 text-[11px] font-medium tracking-[0.08em] text-gray-9 uppercase"
        >
          Suggested
        </motion.p>
        <div className="flex flex-col gap-1.5">
          {data.SuggestedQuestions.objects.map((question, index) => {
            const localizedText =
              question.questionText.myTranslation || question.questionText.en;

            return (
              <motion.div
                key={question.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: 0.15 + index * 0.06,
                  duration: 0.3,
                  ease: [0.4, 0, 0.2, 1],
                }}
              >
                <QuestionCard
                  questionId={question.id}
                  questionText={localizedText}
                  onSelect={onQuestionSelect}
                />
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

interface QuestionCardProps {
  questionId: string;
  questionText: string;
  onSelect: (questionId: string, questionText: string) => void;
  className?: string;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  questionId,
  questionText,
  onSelect,
  className = "",
}) => {
  const handleClick = () => {
    onSelect(questionId, questionText);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        "group flex w-full items-start gap-2 rounded-xl border border-gray-3 bg-white px-3 py-2.5 text-left",
        "transition-all duration-200 ease-out",
        "hover:border-gray-4 hover:bg-gray-1 hover:shadow-sm",
        "active:bg-gray-2",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
        className,
      )}
    >
      <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
        <Sparkles2OutlineIcon className="!size-3" />
      </span>
      <span className="flex-1 text-[13px] leading-[18px] font-medium text-gray-12">
        {questionText}
      </span>
    </button>
  );
};

export default SuggestedQuestionsDisplay;
